import React, {useEffect, useState} from "react";
import HistoryIcon from "../../../../data/themes/history-icon/solid";
import {classNames, getLookup} from "../../../util/util-helpers";

import {
    ACCOUNTING_DOCUMENT_ADDRESS,
    ACCOUNTING_DOCUMENT_LOGO,
    ACCOUNTING_DOCUMENT_NAME,
    DEFAULT_CRUD_STATE,
    DISPATCH_DOCUMENT_ADDRESS,
    DISPATCH_DOCUMENT_LOGO,
    DISPATCH_DOCUMENT_NAME,
    LOAD_STATUSES
} from "../../../../util/util-constants";
import TableCard from "../../resource-table/table-components/table-card";
import TableFilters from "../../resource-table/table-components/table-filters";
import ResourceTable from "../../resource-table";
import Pagination from "../../resource-table/table-components/pagination";
import NoRecordsTable from "../../no-records-found/no-records-table";
import Tippy from "@tippyjs/react";
import {AdjustmentsVerticalIcon, Bars4Icon, CodeBracketIcon, ArrowRightIcon} from "@heroicons/react/24/outline";
import {toFrontDate, toFrontDateTime, toFrontDateTimeFromUTC, toLocalTimeFromUTC} from "../../../util/util-dates";
import {CheckCircleIcon, XCircleIcon} from "@heroicons/react/20/solid";
import {numberWithCommas} from "../../../util/util-formaters";
import {genericMoneyFormatter} from "../../../util/util-vanilla";
import ModalFooter from "../modal-footer";
import {Field, FieldsManager} from "../../../../data/services/fields";
import {WorkingHoursColumn} from "../../display-data/working-hours-display";
import ButtonClose from "../../button/button-close";
import ImageAvatarLoader from "../../image/image-avatar-loader";

const processData = (item, key, type, translate, inTable) => {
    const value = item[key]

    // Custom fields
    if (item.Field === 'CDLState') {
        return getLookup('State')[value]
    }
    if (item.Field === 'DispatchDocumentName') {
        return DISPATCH_DOCUMENT_NAME[value]
    }
    if (item.Field === 'DispatchDocumentAddress') {
        return DISPATCH_DOCUMENT_ADDRESS[value]
    }
    if (item.Field === 'DispatchDocumentLogo') {
        return DISPATCH_DOCUMENT_LOGO[value]
    }
    if (item.Field === 'AccountingDocumentName') {
        return ACCOUNTING_DOCUMENT_NAME[value]
    }
    if (item.Field === 'AccountingDocumentAddress') {
        return ACCOUNTING_DOCUMENT_ADDRESS[value]
    }
    if (item.Field === 'AccountingDocumentLogo') {
        return ACCOUNTING_DOCUMENT_LOGO[value]
    }
    if (item.Field === 'LoadStatusID') {
        return translate(LOAD_STATUSES[value])
    }

    if (item.Field === 'IsBroker') {
        return value ? 'Brokerage' : 'Carrier'
    }

    if (item.Field === 'ShippingHours' || item.Field === 'ReceivingHours') {
        const arr = JSON.parse(value || "[]")

        return !!arr.length && (
            <div className={classNames(inTable ? 'min-w-max' : '',
                "grid divide-x divide-tm-gray-200 grid-cols-3"
            )}>
                <div className="bg-inverse flex items-center flex-col divide-y divide-tm-gray-300">
                    <div className="px-3 font-bold text-tm-gray-700 grow w-full flex items-center h-10">
                        {translate("text.day")}
                    </div>

                    {arr.map(it => (
                        <div className="px-3 grow w-full flex items-center h-10">
                            {it.Day}
                        </div>
                    ))}
                </div>

                <WorkingHoursColumn
                    colTitle={translate("text." + item.Field)}
                    hours={arr}
                    translate={translate}
                />
            </div>
        )
    }

    // System fields
    switch (type) {
        case "date":
            return toFrontDate(value)
        case "datetime":
            return toFrontDateTime(value)
        case "datetimezone":
        case "timezone-custom":
            return toLocalTimeFromUTC(value)
        case 'checkbox':
            return !!value ?
                <div className="pl-4">
                    <CheckCircleIcon className="w-5 h-5 text-green-600"/>
                </div> :
                <div className="pl-4">
                    <XCircleIcon className="w-5 h-5 text-tm-red-400"/>
                </div>
        case 'float':
            return <div className="">{numberWithCommas(value)}</div>
        case 'integer':
            return <div className="">{value ? value.toString().replace(/\D/, '') : ''}</div>
        case 'money':
            return <div className="">{genericMoneyFormatter(value)}</div>
        default:
            return value;
    }
}

export const DEFAULT_HISTORY_TABLE_FIELDS = (translate, fieldTypes = {}) => ({
    UpdatedByContactID: new Field('UpdatedByContactID', '', [], false, 'text', {}),
    Date: new Field('Date', '', [], false, 'datetimezone'),
    Field: new Field('Field', '', [], false, 'custom', {
        render: (item) => translate("field." + item.Field)
    }),
    OldValue: new Field('OldValue', '', [], false, 'text', {
        render: (item) => processData(item, 'OldValue', fieldTypes[item.Field], translate, true),
    }),
    NewValue: new Field('NewValue', '', [], false, 'text', {
        render: (item) => processData(item, 'NewValue', fieldTypes[item.Field], translate, true),
    })
})

const SkeletonBlock = ({className}) => (
    <div className={classNames("bg-tm-gray-200 rounded animate-pulse", className)}/>
);

const HistoryModalSkeleton = () => (
    <div className="grid grid-cols-4">
        {/* Left panel skeleton */}
        <div className="col-span-1 border-r border-tm-gray-200">
            {['w-20', 'w-16', 'w-24', 'w-14', 'w-20'].map((w, i) => (
                <div key={i} className="flex items-center gap-2.5 px-3 py-2.5 border-l-2 border-l-transparent">
                    <SkeletonBlock className="w-8 h-8 rounded-full shrink-0"/>

                    <div className="flex-1 min-w-0 space-y-1.5">
                        <SkeletonBlock className={`h-3 ${w}`}/>

                        <SkeletonBlock className="h-2.5 w-20"/>
                    </div>
                </div>
            ))}
        </div>

        {/* Right panel skeleton */}
        <div className="col-span-3 p-4">
            {/* Detail header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                    <SkeletonBlock className="w-8 h-8 rounded-full shrink-0"/>

                    <div className="space-y-1.5">
                        <SkeletonBlock className="h-3.5 w-32"/>

                        <SkeletonBlock className="h-2.5 w-40"/>
                    </div>
                </div>

                <SkeletonBlock className="h-6 w-20 rounded-full"/>
            </div>

            {/* Field cards */}
            {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-lg border border-tm-gray-200 mb-3 overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-tm-gray-200 bg-tm-gray-50">
                        <SkeletonBlock className="h-3 w-28"/>
                    </div>

                    <div className="flex items-stretch gap-2 p-3">
                        <SkeletonBlock className="flex-1 h-9 rounded-lg"/>

                        <div className="flex items-center px-1 shrink-0">
                            <SkeletonBlock className="w-4 h-4"/>
                        </div>

                        <SkeletonBlock className="flex-1 h-9 rounded-lg"/>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const ModalHistory = ({
                          translate,
                          tableFields,
                          filterFields,
                          showUserAvatar,
                          onClose,
                          title,
                          onFetchData,
                          data,
                          count,
                          isLoading,
                          onFilterInputChange,
                          onRowClick,
                          fieldTypes
                      }) => {

    /** Helpers
     ================================================================= */
    const getQuery = () => {
        return {
            ...query,
            ...FieldsManager.getFieldKeyValues(queryFilterFields),
        }
    }

    const groupBy = (arr, property) => {
        return arr
            .sort((a, b) => new Date(b.Date) - new Date(a.Date))
            .reduce(function (memo, x) {
                if (!memo[x[property]]) {
                    memo[x[property]] = [];
                }
                memo[x[property]].push(x);
                return memo;
            }, {});
    }

    /** State
     ================================================================= */
    const [queryFilterFields, setQueryFilterFields] = useState(() => filterFields ?? {})
    const [query, setQuery] = useState(DEFAULT_CRUD_STATE)
    const [selectedView, setSelectedView] = useState('timeline') // table, timeline
    const [selectedListDate, setSelectedListDate] = useState('')
    const [currentData, setCurrentData] = useState([])

    /** UI Events
     ================================================================= */
    const handleFilterInputChange = (name, value) => {
        const fields = onFilterInputChange
            ? onFilterInputChange(queryFilterFields, name, value)
            : FieldsManager.updateField(queryFilterFields, name, value);
        setQueryFilterFields(fields)
        handleResetPagination()
    }

    const handleResetPagination = () => {
        setQuery((prevState) => ({
            ...prevState,
            offset: 0,
            paginationPage: 1
        }))
    }

    const handleUpdateOffset = (offset, num) => {
        setQuery((prevState) => ({
            ...prevState,
            offset: offset,
            paginationPage: num
        }))
    }

    const handleClose = () => {
        onClose()
    }

    const handleSetSelectedView = (view) => {
        if (view !== selectedView) setSelectedView(view)
    }

    /** Lifecycle
     ================================================================= */
    useEffect(() => {
        if (selectedView === 'table') {
            onFetchData(getQuery())
        }
        if (selectedView === 'timeline') {
            const queryTmp = Object.assign({}, getQuery())
            queryTmp.limit = 500
            queryTmp.offset = 0

            onFetchData(queryTmp)
        }
    }, [queryFilterFields, query, selectedView])

    useEffect(() => {
        if (!isLoading && !!data.length && selectedView === 'timeline') {
            const first = Object.keys(groupedData)[0]
            if (first) {
                setCurrentData(groupedData[first])
                setSelectedListDate(first)
            }
        }
    }, [isLoading])

    /** Render
     ================================================================= */
    const groupedData = groupBy(data, "Date")
    const dates = selectedView === "timeline" && !isLoading && Object.keys(groupedData).map((item, index) => {
        return (
            <button
                key={index}
                onClick={() => {
                    setCurrentData(groupedData[item])
                    setSelectedListDate(item)
                }}
                className={classNames(
                    "cursor-pointer flex items-center gap-2.5 w-full px-3 py-2.5 text-left border-l-2 transition-colors",
                    selectedListDate === item
                        ? "border-l-primary bg-primary-transparent"
                        : "border-l-transparent hover:bg-tm-gray-50"
                )}
            >
                <ImageAvatarLoader
                    ContactID={groupedData[item][0]["ContactID"]}
                    size="sm"
                    hasImagePath={showUserAvatar}
                />

                <div className="min-w-0">
                    <div className="text-xs font-medium text-tm-gray-900 truncate">
                        {groupedData[item][0]["UpdatedByContactID"]}
                    </div>

                    <div className="text-xs text-tm-gray-500 mt-0.5">
                        {toFrontDateTimeFromUTC(item)}
                    </div>
                </div>
            </button>
        )
    })
    const selectedItems = currentData.filter(item => !!item.OldValue || !!item.NewValue);
    const selectedUser = currentData[0]?.UpdatedByContactID ?? '';
    const selectedContactID = currentData[0]?.ContactID;

    const currentDataHtml = selectedView === "timeline" && !isLoading && (
        <div className="p-4">
            {/* Detail header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                    <ImageAvatarLoader
                        key={selectedContactID}
                        ContactID={selectedContactID}
                        size="sm"
                        hasImagePath={showUserAvatar}
                    />

                    <div>
                        <div className="text-sm font-medium text-tm-gray-900">{selectedUser}</div>

                        <div className="text-xs text-tm-gray-500">{toFrontDateTimeFromUTC(selectedListDate)}</div>
                    </div>
                </div>

                <span className="text-xs font-medium text-tm-gray-500 bg-tm-gray-100 px-2.5 py-1 rounded-full shrink-0">
                    {selectedItems.length} {translate('text.changes')}
                </span>
            </div>

            {/* Field change cards */}
            {selectedItems.map((item, index) => (
                <div key={index} className="rounded-lg border border-tm-gray-200 mb-3 overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-tm-gray-200 bg-tm-gray-50">
                        <CodeBracketIcon className="w-3.5 h-3.5 text-tm-gray-400 shrink-0"/>

                        <span className="text-xs text-tm-gray-400">field.</span>

                        <span className="text-xs font-semibold text-tm-gray-700">{translate("field." + item.Field)}</span>
                    </div>

                    <div className="flex items-stretch gap-2 p-3">
                        <div
                            className="flex-1 rounded-lg border px-3 py-2 text-sm flex items-center"
                            style={{backgroundColor: '#FEF2F2', borderColor: '#FCA5A5', color: '#B91C1C'}}
                        >
                            {!item.OldValue
                                ? <em className="not-italic opacity-60 text-xs">{translate('text.empty')}</em>
                                : processData(item, 'OldValue', fieldTypes[item.Field], translate)
                            }
                        </div>

                        <div className="flex items-center px-1 shrink-0">
                            <ArrowRightIcon className="w-4 h-4 text-tm-gray-400"/>
                        </div>

                        <div
                            className="flex-1 rounded-lg border px-3 py-2 text-sm flex items-center"
                            style={{backgroundColor: '#F0FDF4', borderColor: '#86EFAC', color: '#15803D'}}
                        >
                            {processData(item, 'NewValue', fieldTypes[item.Field], translate)}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="relative">
            <header className="p-4 flex items-center gap-3 border-b border-tm-gray-200 text-tm-gray-900">
                <div className="p-1.5 rounded-lg bg-primary-transparent text-primary shrink-0">
                    <HistoryIcon className="w-5 h-5"/>
                </div>

                <div className="flex-1 min-w-0">
                    <h2 className="text-lg text-current truncate">{title}</h2>

                    <p className="text-xs text-tm-gray-500 mt-0.5 leading-tight">
                        {translate("text.tracking_all_field_changes_over_time")}
                    </p>
                </div>

                <ButtonClose onClose={handleClose}/>
            </header>

            <div>
                <div>
                    {selectedView === 'table' && (
                        <TableCard addClass={'relative h-dialog-body'}>
                            <TableFilters
                                filterFields={queryFilterFields}
                                handleInputChange={handleFilterInputChange}
                                translate={translate}
                            />

                            <ResourceTable
                                data={data}
                                fields={tableFields}

                                translate={translate}
                                isLoading={isLoading}

                                limit={queryFilterFields.limit.value}

                                onRowClick={onRowClick}
                            />

                            {/*Table footer*/}
                            {!(!data.length && !isLoading) && (
                                <div
                                    className="bg-inverse px-4 py-3 flex items-center justify-between border-t border-tm-gray-300 sm:px-6 h-16 rounded-b-lg"
                                >
                                    <Pagination
                                        count={count}
                                        isLoading={isLoading}
                                        handleQueryChange={
                                            (name, value, currentPage) => name === "offset"
                                                ? handleUpdateOffset(value, currentPage)
                                                : handleFilterInputChange(name, value)
                                        }
                                        pageOffset={query.offset}
                                        queryFields={queryFilterFields}
                                        translate={translate}
                                        hideRowsPerPage
                                    />
                                </div>
                            )}

                            <NoRecordsTable
                                show={(data.length === 0) && !isLoading}
                                title={'No matching records found'}
                            />
                        </TableCard>
                    )}

                    {selectedView === 'timeline' && isLoading && <HistoryModalSkeleton/>}

                    {selectedView === 'timeline' && !isLoading && (
                        <div>
                            <div className="flex items-center border-b border-tm-gray-200">
                                <div className="flex-1">
                                    <TableFilters
                                        filterFields={queryFilterFields}
                                        handleInputChange={handleFilterInputChange}
                                        translate={translate}
                                        hideLimit
                                    >
                                        <div className="flex items-center">
                                        <span
                                            className="text-xs font-medium text-tm-gray-500 bg-tm-gray-100 px-2.5 py-1 rounded-full shrink-0 mx-4">
                                    {Object.keys(groupedData).length} {translate('text.entries')}
                                </span>
                                        </div>
                                    </TableFilters>
                                </div>
                            </div>
                            <div className={'grid grid-cols-4'}>
                                <div className={'px-0 py-5 col-span-1 max-h-[calc(100vh-16rem)] overflow-auto'}>
                                    {dates}
                                </div>

                                <div className={'border-l border-gray-200 col-span-3 max-h-[calc(100vh-16rem)] overflow-auto'}>
                                    {currentDataHtml}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <ModalFooter
                closeButtonLabel={translate('btn.close')}
                onClose={handleClose}
            >
                {!!data.length && (
                    <div className="">
                        <Tippy content={translate('text.timeline_view')}>
                            <button
                                onClick={() => handleSetSelectedView('timeline')}
                                className={classNames(selectedView === 'timeline' ? 'text-primary' : 'text-tm-gray-400 bg-tm-gray-200', "h-full rounded-l-input relative inline-flex items-center px-4 py-2 border border-tm-gray-300 bg-field text-sm font-medium focus:z-10 focus:outline-hidden focus:ring-1")}
                            >
                                <AdjustmentsVerticalIcon className="h-5 w-5"/>
                            </button>
                        </Tippy>
                        <Tippy content={translate('text.table_view')}>
                            <button
                                onClick={() => handleSetSelectedView('table')}
                                className={classNames(selectedView === 'table' ? 'text-primary' : 'text-tm-gray-400 bg-tm-gray-200', "h-full rounded-r-input  relative inline-flex items-center px-4 py-2 border border-tm-gray-300 bg-field text-sm font-medium focus:z-10 focus:outline-hidden focus:ring-1")}
                            >
                                <Bars4Icon className="h-5 w-5"/>
                            </button>
                        </Tippy>
                    </div>
                )}
            </ModalFooter>
        </div>
    )
}

export default ModalHistory
